import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, Send, Clock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { JobFinder } from '@/components/updateResend/JobFinder';
import { UpdateCards } from '@/components/updateResend/UpdateCards';
import { DocPicker } from '@/components/updateResend/DocPicker';

export default function UpdateResendPage() {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();
  const [jobId, setJobId] = useState(params.get('job') || '');
  const [pendingId, setPendingId] = useState(params.get('pending') || '');
  const [snap, setSnap] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [diff, setDiff] = useState({ changes: [], suggested_docs: [], changes_summary: '' });
  const [picked, setPicked] = useState([]);
  const [email, setEmail] = useState({ to: '', subject: '', body: '' });
  const [tpl, setTpl] = useState({ subject: '', body: '' });
  const [remindOn, setRemindOn] = useState(new Date(Date.now() + 86400000).toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' }));
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState('');

  const render = useCallback(async (jid, t, summary) => {
    const { data } = await api.post('/update-resend/render', { job_id: jid, subject: t.subject, body: t.body, changes_summary: summary });
    setEmail((e) => ({ ...e, subject: data.subject, body: data.body }));
  }, [api]);

  const loadJob = useCallback(async (jid, pending) => {
    setBusy('load');
    try {
      const { data } = await api.get(`/update-resend/job/${jid}`);
      setSnap(data); setTpl(data.script);
      if (pending) {
        setBaseline(pending.snapshot && pending.snapshot.job ? pending.snapshot : data);
        setDiff({ changes: pending.changes || [], suggested_docs: pending.doc_keys || [], changes_summary: '' });
        setPicked(pending.doc_keys || []);
        setEmail({ to: (pending.to || []).join(', '), subject: pending.subject, body: pending.body });
        setRemindOn(pending.remind_on || remindOn);
      } else {
        setBaseline(data); setPicked([]); setDiff({ changes: [], suggested_docs: [], changes_summary: '' });
        setEmail({ to: data.client.email || '', subject: '', body: '' });
        await render(jid, data.script, '');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load job'); }
    finally { setBusy(''); }
  }, [api, render]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pendingId) api.get(`/update-resend/pending/${pendingId}`).then(({ data }) => { setJobId(data.job_id); loadJob(data.job_id, data); }).catch(() => toast.error('Pending item not found'));
    else if (jobId) loadJob(jobId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (jid) => { setJobId(jid); setPendingId(''); setEdited(false); setParams({ job: jid }); loadJob(jid); };

  const checkChanges = async () => {
    setBusy('diff');
    try {
      const { data } = await api.post('/update-resend/diff', { snapshot: baseline });
      setDiff(data); setSnap((s) => ({ ...s, documents: data.documents }));
      setPicked((p) => Array.from(new Set([...p, ...data.suggested_docs])));
      if (!edited && !pendingId) await render(jobId, tpl, data.changes_summary);
      toast.message(data.changes.length ? `${data.changes.length} change(s) found` : 'No changes found yet');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not check changes'); }
    finally { setBusy(''); }
  };

  // After an inline form saves or a focused pop-out closes: reload the job, then find what changed.
  const refreshAfterEdit = async () => {
    try {
      const { data } = await api.get(`/update-resend/job/${jobId}`);
      setSnap((s) => ({ ...data, documents: s?.documents || data.documents }));
    } catch { /* diff below still runs */ }
    await checkChanges();
  };

  const docs = (snap?.documents || []).filter((d) => picked.includes(d.key));  const recipients = email.to.split(',').map((x) => x.trim()).filter(Boolean);

  const sendNow = async () => {
    if (!recipients.length || !docs.length) { toast.error('Add a recipient and pick at least one document'); return; }
    setBusy('send');
    try {
      await api.post('/email/send-bundle', { to: recipients, subject: email.subject, body: email.body, appraisal_id: jobId, event: 'update_resend',
        attachments: docs.map((d) => ({ route: d.route, filename: `${d.label}.pdf` })) });
      await api.post('/update-resend/complete', { job_id: jobId, pending_id: pendingId || null, to: recipients, subject: email.subject,
        body: email.body, documents: docs.map((d) => d.label), changes: diff.changes });
      toast.success('Sent — logged on the job history (stage unchanged)');
      setPendingId(''); setBaseline(snap);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(''); }
  };

  const later = async () => {
    setBusy('later');
    try {
      const { data } = await api.post('/update-resend/pending', { id: pendingId || null, job_id: jobId, remind_on: remindOn, to: recipients,
        subject: email.subject, body: email.body, doc_keys: picked, changes: diff.changes, snapshot: baseline });
      setPendingId(data.id);
      toast.success(`Saved to "To finish" for ${remindOn}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4 max-w-6xl" data-testid="update-resend-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Update & Resend</h1>
        <p className="text-sm text-slate-500 mt-1">Quickly update a job's details and resend only the documents that need it — now or later. The workflow stage never moves.</p>
      </div>
      {!snap && <JobFinder onPick={pick} clientId={params.get('client')} propertyId={params.get('property')} />}
      {busy === 'load' && <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading job…</div>}
      {snap && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 bg-[#0F4F1B] text-white rounded-md px-4 py-3" data-testid="ur-job-header">
            <div className="text-sm"><b className="font-mono">A{snap.job.appraisal_no}</b> · {snap.client.name} · {snap.property.address} · {snap.quotes.length} quote(s)
              {pendingId && <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-amber-400 text-slate-900">finishing a saved resend</span>}</div>
            <button data-testid="ur-change-job-btn" onClick={() => { setSnap(null); setParams({}); setPendingId(''); }} className="text-xs inline-flex items-center gap-1 hover:underline"><RotateCcw size={12} /> Change job</button>
          </div>
          <UpdateCards snap={snap} onRefresh={refreshAfterEdit} />
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4" data-testid="ur-changes">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-slate-800">What's changed</div>
              <button data-testid="ur-check-changes-btn" onClick={checkChanges} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#D35400] text-white hover:bg-[#b84a00]">
                {busy === 'diff' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Check what changed
              </button>
            </div>
            {diff.changes.length ? <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5" data-testid="ur-change-list">{diff.changes.map((c) => <li key={c}>{c}</li>)}</ul>
              : <div className="text-xs text-slate-400">Edit in the cards above — changes are checked automatically when you Save &amp; return (or click "Check what changed").</div>}
          </div>
          <DocPicker documents={snap.documents} picked={picked} setPicked={setPicked} suggested={diff.suggested_docs} />
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-2" data-testid="ur-email">
            <div className="text-sm font-bold text-slate-800">Email</div>
            <input data-testid="ur-email-to" value={email.to} onChange={(e) => setEmail({ ...email, to: e.target.value })} placeholder="To (comma separated)" className="w-full px-3 py-2 text-sm border border-[#E2E2DF] rounded" />
            <input data-testid="ur-email-subject" value={email.subject} onChange={(e) => { setEdited(true); setEmail({ ...email, subject: e.target.value }); }} className="w-full px-3 py-2 text-sm border border-[#E2E2DF] rounded" />
            <textarea data-testid="ur-email-body" rows={9} value={email.body} onChange={(e) => { setEdited(true); setEmail({ ...email, body: e.target.value }); }} className="w-full px-3 py-2 text-sm border border-[#E2E2DF] rounded font-mono" />
            <p className="text-[11px] text-slate-400">From the "Update & Resend" script (edit it in Communication Scripts). {'{{changes_summary}}'} fills with the change summary.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3" data-testid="ur-actions">
            <label className="text-xs text-slate-600 flex items-center gap-2">Remind me on
              <input type="date" data-testid="ur-remind-on" value={remindOn} onChange={(e) => setRemindOn(e.target.value)} className="border border-[#E2E2DF] rounded px-2 py-1" />
            </label>
            <button data-testid="ur-resend-later-btn" onClick={later} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md border border-[#E2E2DF] bg-white hover:bg-slate-50">
              {busy === 'later' ? <Loader2 size={13} className="animate-spin" /> : <Clock size={13} />} Resend later
            </button>
            <button data-testid="ur-send-now-btn" onClick={sendNow} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] disabled:opacity-50">
              {busy === 'send' ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send now ({docs.length} doc{docs.length === 1 ? '' : 's'})
            </button>
          </div>
        </>
      )}
    </div>
  );
}
